/**
 * The document tab strip — SPEC.md §13's "Multi-diagram documents / tabs"
 * stretch goal, now built (see tabs.ts's own doc comment for the additive
 * design). Always visible, one row below the topbar: the active buffer's
 * own tab first, then each backgrounded tab in `plotter:tabs` order, then a
 * trailing "+" to park the current buffer and start a fresh one.
 *
 * Mirrors PaneTabs.tsx's `role="tablist"`/`role="tab"` shape (the WAI-ARIA
 * tabs pattern), extended with a close button per tab — PaneTabs' own two
 * fixed Source/Preview tabs never close, so it had no need for one.
 */
import { Plus, X } from 'lucide-react';
import type { BackgroundTab } from '../lib/tabs';

interface DocumentTabsProps {
  activeTitle: string;
  tabs: BackgroundTab[];
  onSelect: (tab: BackgroundTab) => void;
  onCloseActive: () => void;
  onClose: (tab: BackgroundTab) => void;
  onNew: () => void;
}

export default function DocumentTabs({ activeTitle, tabs, onSelect, onCloseActive, onClose, onNew }: DocumentTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Open diagrams"
      className="flex shrink-0 items-center gap-0.5 overflow-x-auto border-b border-ink-3 bg-ink-2 px-2"
    >
      <div
        role="tab"
        aria-selected="true"
        className="flex shrink-0 items-center gap-1.5 border-b-2 border-pink px-2 py-1.5 text-xs text-text-ink"
      >
        <span className="max-w-[16ch] truncate">{activeTitle}</span>
        {/* Closing the active tab is only offered once there's somewhere
            else to land — a lone tab has nothing to close *to* beyond
            starting over, which the "+" button already covers explicitly. */}
        {tabs.length > 0 && (
          <button
            type="button"
            aria-label={`Close ${activeTitle}`}
            onClick={onCloseActive}
            className="rounded-sm p-0.5 text-mute hover:bg-ink-3 hover:text-text-ink"
          >
            <X size={11} aria-hidden />
          </button>
        )}
      </div>

      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tab"
          aria-selected="false"
          className="flex shrink-0 items-center gap-1.5 border-b-2 border-transparent px-2 py-1.5 text-xs text-mute hover:text-text-ink"
        >
          <button type="button" onClick={() => onSelect(tab)} className="max-w-[16ch] truncate text-left">
            {tab.title}
          </button>
          <button
            type="button"
            aria-label={`Close ${tab.title}`}
            onClick={() => onClose(tab)}
            className="rounded-sm p-0.5 hover:bg-ink-3 hover:text-text-ink"
          >
            <X size={11} aria-hidden />
          </button>
        </div>
      ))}

      <button
        type="button"
        // Not "New tab" — Playwright's (and every other a11y tooling's)
        // accessible-name matching is substring-based without an explicit
        // exact match, and the topbar already has a "New" menu button
        // (NewMenu.tsx); "New tab" would ambiguously match both.
        aria-label="Add tab"
        onClick={onNew}
        className="ml-1 shrink-0 rounded-sm p-1 text-mute hover:bg-ink-3 hover:text-text-ink"
      >
        <Plus size={13} aria-hidden />
      </button>
    </div>
  );
}
